<script setup lang="ts">
import {ref, computed} from 'vue';
import {groupContainsPage, type SchematicHierarchyGroup} from './schematic-hierarchy.ts';
import type {TreeNodeWithPage} from './diff-grouping.ts';
import {formatGroupName} from './diff-grouping.ts';
import type {SchemaType} from '../../../features/allspicejson.ts';
import DiffItem from './DiffItem.vue';
import {SvgIcon} from '../../../svg.ts';

const props = withDefaults(defineProps<{
  group: SchematicHierarchyGroup;
  changesByPageId: Map<string, Record<string, TreeNodeWithPage[]>>;
  schemaType: SchemaType;
  pageNameMap: Record<string, string>;
  diffTree: TreeNodeWithPage;
  navigationRootId?: string;
  depth?: number;
}>(), {
  navigationRootId: undefined,
  depth: 0,
});

const isExpanded = ref(true);

const groupChanges = computed(() => {
  const result: Record<string, TreeNodeWithPage[]> = {};
  for (const page of props.group.pages) {
    const pageChanges = props.changesByPageId.get(page.pageId);
    if (!pageChanges) continue;
    for (const [entityGroup, changes] of Object.entries(pageChanges)) {
      if (!result[entityGroup]) {
        result[entityGroup] = [];
      }
      result[entityGroup].push(...changes);
    }
  }
  return result;
});

const childGroups = computed((): SchematicHierarchyGroup[] => {
  const children: SchematicHierarchyGroup[] = [];
  for (const page of props.group.pages) {
    children.push(...page.childGroups);
  }
  children.push(...props.group.unanchoredChildGroups);
  return children;
});

const visible = computed(() => groupContainsPage(props.group, (id) => props.changesByPageId.has(id)));

function toggle() {
  isExpanded.value = !isExpanded.value;
}
</script>

<template>
  <div
    v-if="visible"
    :style="depth > 0 ? { paddingLeft: '0.75rem' } : undefined"
  >
    <div
      class="tw:flex tw:items-center tw:gap-1 tw:cursor-pointer tw:py-1 tw:px-2 tw:hover:bg-hover tw:select-none tw:rounded"
      @click="toggle"
    >
      <SvgIcon
        :name="isExpanded ? 'octicon-chevron-down' : 'octicon-chevron-right'"
        :size="16"
        class="tw:flex-shrink-0"
      />
      <span class="tw:text-sm tw:font-semibold tw:truncate">
        {{ group.displayName }}
      </span>
    </div>
    <div v-if="isExpanded">
      <template
        v-for="(changes, entityGroupName) of groupChanges"
        :key="entityGroupName"
      >
        <div class="tw:text-base tw:font-bold tw:ml-2">
          {{ formatGroupName(schemaType, entityGroupName) }}
        </div>
        <DiffItem
          v-for="change of changes"
          :key="`${change.page}-${change.key}`"
          :item="change"
          :group-name="entityGroupName"
          :page-name="pageNameMap[change.page]"
          :schema-type="schemaType"
          :diff-tree="diffTree"
          :navigation-root-id="navigationRootId"
        />
      </template>
      <DiffHierarchyGroup
        v-for="child of childGroups"
        :key="child.groupId"
        :group="child"
        :changes-by-page-id="changesByPageId"
        :schema-type="schemaType"
        :page-name-map="pageNameMap"
        :diff-tree="diffTree"
        :navigation-root-id="navigationRootId"
        :depth="depth + 1"
      />
    </div>
  </div>
</template>
